import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import { getApiUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { styled } from '@mui/material/styles';

const useStyles = makeStyles({
  root: {
    maxWidth: 600,
    margin: '0 auto',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  actions: {
    justifyContent: 'flex-end',
    gap: '12px',
  },
  fileUpload: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
});

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default function UploadRequestForm({ onSuccess, onCancel }) {
  const classes = useStyles();
  const { authFetch } = useAuth();
  const [formData, setFormData] = useState({
    requestName: '',
    description: '',
    exactRequest: '',
    visible: false,
    zipFile: null
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadJob, setUploadJob] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobLogs, setJobLogs] = useState([]);
  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isZip = file.type === 'application/zip' || file.name.endsWith('.zip');
      const isTgz = file.name.endsWith('.tgz') || file.name.endsWith('.tar.gz');

      if (!isZip && !isTgz) {
        setError('Please select a valid ZIP or TGZ file');
        return;
      }
      setFormData({
        ...formData,
        zipFile: file
      });
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (!formData.requestName.trim()) {
      setError('Request name is required');
      setLoading(false);
      return;
    }

    if (!formData.description.trim()) {
      setError('Description is required');
      setLoading(false);
      return;
    }

    if (!formData.exactRequest.trim()) {
      setError('Exact request is required');
      setLoading(false);
      return;
    }

    if (!formData.zipFile) {
      setError('Archive file is required');
      setLoading(false);
      return;
    }

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('requestName', formData.requestName);
      uploadFormData.append('description', formData.description);
      uploadFormData.append('exactRequest', formData.exactRequest);
      uploadFormData.append('visible', formData.visible);
      uploadFormData.append('zipFile', formData.zipFile);

      const response = await authFetch(getApiUrl('/api/admin/requests'), {
        method: 'POST',
        body: uploadFormData
      });

      if (response.ok) {
        const result = await response.json();
        setUploadJob(result);
        setSuccess('Upload queued for processing!');

        // Start polling for job status
        pollJobStatus(result.jobId);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Upload failed');
      }
    } catch (error) {
      setError('Network error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  const pollJobStatus = async (jobId) => {
    stopPolling();

    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await authFetch(getApiUrl(`/api/admin/upload-jobs/${jobId}`));

        if (response.ok) {
          const data = await response.json();
          setJobStatus(data.job);
          setJobLogs(data.logs);

          if (data.job.status === 'completed') {
            stopPolling();
            setLoading(false);
            setSuccess('Processing completed successfully! The request has been added to your records.');
          } else if (data.job.status === 'failed') {
            stopPolling();
            setLoading(false);
            setError(`Processing failed: ${data.job.error_message}. All changes have been rolled back.`);
          }
        }
      } catch (error) {
        console.error('Error polling job status:', error);
      }
    }, 1000);

    // Clear interval after 10 minutes to prevent indefinite polling
    pollTimeoutRef.current = setTimeout(() => stopPolling(), 600000);
  };

  const handleStartNewUpload = () => {
    stopPolling();
    // Reset all form state
    setFormData({
      requestName: '',
      description: '',
      exactRequest: '',
      visible: false,
      zipFile: null
    });
    setUploadJob(null);
    setJobStatus(null);
    setJobLogs([]);
    setError('');
    setSuccess('');
    setLoading(false);
  };

  const handleReturnToList = () => {
    onSuccess && onSuccess(uploadJob);
  };

  const handleCancel = () => {
    onCancel && onCancel();
  };

  return (
    <Card className={classes.root} variant="outlined">
      <CardContent>
        <Typography variant="h5" component="h2" gutterBottom>
          Upload New Request
        </Typography>
        <Typography variant="body2" component="p" gutterBottom>
          Upload a new public records request with supporting files
        </Typography>

        {error && (
          <Alert severity="error" style={{ marginBottom: '16px' }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" style={{ marginBottom: '16px' }}>
            {success}
          </Alert>
        )}

        {uploadJob && jobStatus && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Processing Status: {jobStatus.status}
            </Typography>

            {jobStatus.status === 'processing' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={jobStatus.progress || 0}
                  sx={{ flexGrow: 1 }}
                />
                <Typography variant="body2">
                  {jobStatus.progress || 0}%
                </Typography>
              </Box>
            )}

            {jobStatus.status === 'queued' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Waiting to start...</Typography>
              </Box>
            )}

            {jobStatus.status === 'completed' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircleIcon color="success" />
                <Typography variant="body2" color="success.main">
                  Processing completed successfully
                </Typography>
              </Box>
            )}

            {jobStatus.status === 'failed' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ErrorIcon color="error" />
                <Typography variant="body2" color="error.main">
                  Processing failed
                </Typography>
              </Box>
            )}

            {jobLogs.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2">Recent Logs:</Typography>
                <Box sx={{ maxHeight: 150, overflow: 'auto', bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                  {jobLogs.slice(0, 5).map((log, index) => (
                    <Typography key={index} variant="caption" display="block"
                      color={log.level === 'error' ? 'error.main' : 'text.secondary'}>
                      {new Date(log.created_at).toLocaleTimeString()}: {log.message}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        <form className={classes.form} onSubmit={handleSubmit}>
          <TextField
            name="requestName"
            label="Request Name"
            value={formData.requestName}
            onChange={handleChange}
            required
            fullWidth
            variant="outlined"
            helperText="A short, descriptive name for this request"
          />

          <TextField
            name="description"
            label="Simple Description"
            value={formData.description}
            onChange={handleChange}
            required
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            helperText="A brief description of what this request contains"
          />

          <TextField
            name="exactRequest"
            label="Exact Request"
            value={formData.exactRequest}
            onChange={handleChange}
            required
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            helperText="The exact text of the original public records request"
          />

          <div className={classes.fileUpload}>
            <Typography variant="subtitle1" component="label">
              Archive File Upload *
            </Typography>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{ alignSelf: 'flex-start' }}
            >
              Choose Archive File
              <VisuallyHiddenInput
                type="file"
                accept=".zip,.tgz,.tar.gz,application/zip,application/gzip"
                onChange={handleFileChange}
              />
            </Button>
            {formData.zipFile && (
              <Typography variant="body2" color="text.secondary">
                Selected: {formData.zipFile.name} ({(formData.zipFile.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              Upload the ZIP or TGZ file containing the message data and any supporting documents
            </Typography>
          </div>

          <FormControlLabel
            control={
              <Switch
                name="visible"
                checked={formData.visible}
                onChange={handleChange}
                color="primary"
              />
            }
            label="Make Publicly Visible"
            helperText="Toggle whether this request should be immediately visible to the public"
          />
        </form>
      </CardContent>
      <CardActions className={classes.actions}>
        {jobStatus && (jobStatus.status === 'completed' || jobStatus.status === 'failed') ? (
          // Show completion actions
          <>
            <Button
              variant="outlined"
              onClick={handleStartNewUpload}
            >
              Start New Upload
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleReturnToList}
            >
              Return to Requests List
            </Button>
          </>
        ) : (
          // Show normal form actions
          <>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading || (uploadJob && jobStatus)}
            >
              {loading ? 'Uploading...' : 'Upload Request'}
            </Button>
          </>
        )}
      </CardActions>
    </Card>
  );
}